/* eslint-disable jsx-a11y/anchor-is-valid */

import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { useFormik } from 'formik'
import { login } from '../core/_requests'
import { useAuth } from '../core/Auth'
import { loginSchema } from './validation/loginSchema'
import { isMfaDesafio, UsuarioDtoLogin } from '../core/_models'
import { getPushFid } from '../../apps/notificacoes/push/getPushFid'
import {
  AuthTrustFooter,
  Button,
  ContactSupportLink,
  FormInput,
  LoadingIndicator,
} from '../../../components'
import { ButtonType, PlataformaType } from '../../../../_metronic/helpers'
import { getCompanyLoginPath, useLoginCompanyConfig } from '../core/loginCompanyConfig'
import { saveLoginEmpresaId, saveLoginRedirectPath } from '../core/AuthHelpers'
import { TwoFactorState, useConcluirLogin } from '../core/twoFactor'
import { consumirSessaoExpirada } from '../core/sessaoExpirada'

const LOGIN_ERROR_FALLBACK = 'Não foi possível entrar. Tente novamente em instantes.'

type LoginErrorResponse = {
  data?: {
    message?: string
  }
}

// 401/500 trazem a mensagem em data.message; 422 de campo não chega aqui (Formik valida antes)
const getLoginErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as LoginErrorResponse | undefined)?.data?.message
    if (message) return message
  }
  return LOGIN_ERROR_FALLBACK
}

export const Login = () => {
  const { saveAuth } = useAuth()
  const concluirLogin = useConcluirLogin()

  const navigate = useNavigate()
  const { empresa } = useParams<{ empresa?: string }>()

  const [searchParams] = useSearchParams()
  const alterSenha = String(searchParams.get('alter'))
  // Lido uma vez na montagem: o aviso não volta depois de um F5 nem de nova tentativa.
  const [sessaoExpirada] = useState(consumirSessaoExpirada)

  const { data: loginCompanyConfig } = useLoginCompanyConfig(empresa)

  const initialValues = {
    email: '',
    password: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true)

      try {
        // Pedido aqui, dentro do submit, porque navegadores como o Firefox só mostram o pedido de
        // permissão de notificação quando ele parte de uma ação do usuário.
        const pushToken = await getPushFid({ pedirPermissao: true })

        const credentials: UsuarioDtoLogin = {
          email: values.email,
          senha: values.password,
          plataforma: PlataformaType.Site,
          ...(loginCompanyConfig ? { empresa: loginCompanyConfig.id } : {}),
          ...(pushToken ? { pushToken } : {}),
        }
        const resposta = await login(credentials)
        saveLoginEmpresaId(loginCompanyConfig?.id)
        loginCompanyConfig
          ? saveLoginRedirectPath(getCompanyLoginPath(loginCompanyConfig?.slug || empresa))
          : saveLoginRedirectPath()

        if (isMfaDesafio(resposta)) {
          const twoFactorState: TwoFactorState = { desafio: resposta.mfa.token }
          navigate(resposta.mfa.cadastrado ? '/auth/2fa' : '/auth/2fa/cadastro', {
            state: twoFactorState,
          })
          return
        }

        await concluirLogin(resposta)
      } catch (error) {
        saveAuth(undefined)
        setStatus(getLoginErrorMessage(error))
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate autoComplete='off'>
      <div className='card-body'>
        <div className='text-start mb-10'>
          <h1 className='text-dark mb-3 fs-2x' data-kt-translate='sign-in-title'>
            Bem-vindo(a) à {loginCompanyConfig?.name || 'Lauduz'}
          </h1>
          <div className='text-gray-700 fw-semibold fs-3' data-kt-translate='general-desc'>
            Entrar
          </div>
        </div>
        {alterSenha === 'true' && (
          <div className='mb-lg-15 alert alert-success'>
            <div className='text-success font-weight-bold'>Senha Alterada com sucesso</div>
          </div>
        )}

        {sessaoExpirada && !formik.status && (
          <div className='mb-lg-15 alert alert-warning' role='alert'>
            <div className='alert-text font-weight-bold'>
              Sua sessão expirou. Faça login novamente para continuar.
            </div>
          </div>
        )}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger' role='alert'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}
        <FormInput
          formik={formik}
          placeholder='Digite seu e-mail'
          name='email'
          label='E-mail'
          className='mb-8'
          isRequired
          autoComplete='off'
        />
        <FormInput
          formik={formik}
          type='password'
          placeholder='Digite sua senha'
          name='password'
          label='Senha'
          className='mb-8'
          isRequired
          autoComplete='new-password'
        />

        <div className='d-flex flex-end mb-10'>
          <Button
            onClick={() => navigate('/auth/forgot-password')}
            className='m-0 px-2 py-2 fs-6 fw-bolder'
            disabled={formik.isSubmitting}
          >
            Esqueceu sua senha?
          </Button>
        </div>

        <Button
          type={ButtonType.Submit}
          className='btn-primary btn-auth-full'
          disabled={formik.isSubmitting}
          aria-label='Entrar'
        >
          {formik.isSubmitting ? <LoadingIndicator /> : 'Entrar'}
        </Button>

        <div className='text-center mt-6'>
          <ContactSupportLink />
        </div>

        <AuthTrustFooter />
      </div>
    </form>
  )
}
